/**
 * Admin Portal → Codes → Overview — `/admin/codes` (Codes PR-8; the lead's unified-codes design §2.1).
 *
 * Per family: how many codes, issued, redeemed, expired (the unified words). What needs an eye: App
 * Store code stock running low or expiring, an API key near its daily quota, a product's offer slots
 * nearly full. And the two tools the owner uses most, one click away: "+ One-off code" and "Generate
 * pool codes" (both on Issue codes).
 *
 * Every admin role reads; the issue shortcuts show for admin roles only (hidden until the role is
 * known — fail closed).
 *
 * Codes PR-9: the last run of the nightly codes reconcile, and "Run reconcile now" for admin roles
 * (CodesReconcilePanel — the server checks the role again).
 *
 * Web-only (the admin SPA).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AdminCard } from '../../../components/admin/ui';
import { ReadOnlyNote, hint } from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import {
  CODES_ROUTES, UNIFIED_VOCAB_NOTE, attentionItems, familyTiles,
  type ApiSummary, type CatalogSummary, type InventorySummary, type OffersView,
} from './codesAreaLogic';
import { CodesHeader, ErrorLine, PAGE, VocabNote, readModelError, useCodesRole } from './CodesArea';
import { CodesReconcilePanel } from './CodesReconcilePanel';

const tile: React.CSSProperties = { minWidth: 170, flex: '1 1 170px' };

export function CodesOverview() {
  const { role, loaded, writable } = useCodesRole();
  const [summary, setSummary] = useState<CatalogSummary | null>(null);
  const [inv, setInv] = useState<InventorySummary | null>(null);
  const [api, setApi] = useState<ApiSummary | null>(null);
  const [offers, setOffers] = useState<OffersView | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    CodesAdmin.catalogSummary().then((r) => { if (live) setSummary(r); }).catch((e) => { if (live) setError(readModelError(e)); });
    // The attention list is best-effort: a screen that can't answer leaves its items out, never the page.
    CodesAdmin.inventory().then((r) => { if (live) setInv(r); }).catch(() => undefined);
    CodesAdmin.apiSummary().then((r) => { if (live) setApi(r); }).catch(() => undefined);
    CodesAdmin.offers().then((r) => { if (live) setOffers(r); }).catch(() => undefined);
    return () => { live = false; };
  }, []);

  const tiles = familyTiles(summary);
  const items = attentionItems(inv, api, offers);
  return (
    <div style={PAGE}>
      <CodesHeader
        title="Codes"
        subtitle="Every code Coach Cedar issues, in one place: Summer50, one-off, referral, partner and Coach Cedar codes, and the App Store code supply."
      />
      <ReadOnlyNote writable={writable} loaded={loaded} role={role} subject="codes" />
      {writable && (
        <div data-testid="issue-shortcuts" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <Link to={`${CODES_ROUTES.issue}?open=one-off`} className="admin-btn admin-btn--primary admin-btn--md">+ One-off code</Link>
          <Link to={CODES_ROUTES.issue} className="admin-btn admin-btn--secondary admin-btn--md">Generate pool codes</Link>
          <Link to={CODES_ROUTES.issueHouse} className="admin-btn admin-btn--secondary admin-btn--md">Coach Cedar codes</Link>
        </div>
      )}
      <ErrorLine text={error} />
      <VocabNote text={UNIFIED_VOCAB_NOTE} />
      {summary && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 20 }} data-testid="family-tiles">
          {tiles.map((t) => (
            <AdminCard key={t.family} style={tile}>
              <Link to={`${CODES_ROUTES.all}?family=${encodeURIComponent(t.family)}`} style={{ fontSize: 12, color: 'var(--text-secondary, #6b7280)' }}>{t.label}</Link>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{t.codes.toLocaleString()}</div>
              <div style={{ fontSize: 12 }}>
                {t.available > 0 && <>{t.available.toLocaleString()} available · </>}
                {t.issued.toLocaleString()} issued · {t.redeemed.toLocaleString()} redeemed · {t.expired.toLocaleString()} expired
              </div>
            </AdminCard>
          ))}
        </div>
      )}
      <h2 style={{ fontSize: 16, margin: '8px 0' }}>Needs a look</h2>
      {items.length === 0 ? (
        <p style={hint} data-testid="attention-none">Nothing: stock, quotas and offer slots are all fine.</p>
      ) : (
        <ul data-testid="attention" style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {items.map((a, i) => (
            <li key={i} style={{ color: a.tone === 'warn' ? 'var(--status-warning-text, #92400e)' : undefined, marginBottom: 4 }}>
              {a.text} {a.link && <Link to={a.link}>Open →</Link>}
            </li>
          ))}
        </ul>
      )}
      <CodesReconcilePanel writable={writable} />
      {summary && <p style={{ ...hint, marginTop: 16 }}>Counted {new Date(summary.as_of).toLocaleString()}. Nothing on this page is stored: every count is read from the codes.</p>}
    </div>
  );
}

export default CodesOverview;
